import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Flex,
  Box,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
  useToast,
} from '@chakra-ui/react';
import TextEditModal from './textEditModal';
import { v4 as uuidv4 } from 'uuid';
import { toolboxItems } from '../utils/toolboxItem';
import { useSignalR } from '../hooks/useSignalR';
import { GetAllTags } from '../api/tag';
import FlowSaveModal from './FlowSaveModal';
import { useParams, useNavigate } from 'react-router-dom';
import { getFlowById, getAllFlows as fetchFlows } from '../api/flow';
import FlowAssignModal from './FlowAssignModal';
import AssignRegisterModal from './AssignRegisterModal';
import AlarmsDisplayForFlowComponents from '../pages/alarmDisplayForFlowComponent';
import { IconButton } from '@chakra-ui/react';
import {
  FiChevronLeft,
  FiChevronRight,
  FiSearch,
  FiX,
  FiRotateCcw,
  FiRotateCw,
} from 'react-icons/fi';
// import { findClosestPortSnap } from '../utils/connectionPorts';

// ---------------------------------------------------------------------------
// Nexa-Flow — bileşen boyut/açı sınırları
// ---------------------------------------------------------------------------
// Bir bileşenin genişliği/yüksekliği ya da ölçeği 0'a çekildiğinde canvas'ta
// tamamen kayboluyor ve bir daha seçilemiyordu (tıklanacak piksel kalmıyor).
// Hem context-menu input'ları hem de kenar tutamaklarıyla yapılan yeniden
// boyutlandırma bu alt sınırların altına inemez.
const MIN_ITEM_SIZE = 10;
const MAX_ITEM_SIZE = 2000;
const MIN_ITEM_SCALE = 10;
const MAX_ITEM_SCALE = 300;
// width/height ile scale AYRI AYRI sınırlansa bile birlikte bileşeni yok
// edebilirler (ör. width=10 + scale=%10 => yarım piksel). Bu yüzden asıl
// garanti ekranda kaplanan piksel üzerinden veriliyor: bir bileşenin çizilen
// kenarı hiçbir zaman bu değerin altına inemez.
const MIN_RENDERED_PX = 8;

// Backend FlowItem.rotation/width/height/scale alanları `int` — kesirli değer
// göndermek serileştirmede patlar. Bu yüzden tüm boyut/açı değerleri tam
// sayıya yuvarlanarak saklanıyor (1° hassasiyet mimic ekran için fazlasıyla
// yeterli).
const clampSize = value =>
  Math.min(Math.max(Math.round(value), MIN_ITEM_SIZE), MAX_ITEM_SIZE);
const clampScale = value =>
  Math.min(Math.max(Math.round(value), MIN_ITEM_SCALE), MAX_ITEM_SCALE);
const normalizeRotation = deg => ((Math.round(deg) % 360) + 360) % 360;

// Kaydedilmiş (veya yeni bırakılan) bir item'ı güvenli sınırlara çeker.
// Daha önce 0 boyutuyla kaydedilip "kaybolmuş" bileşenler de böylece geri
// görünür hâle gelir.
const withSafeGeometry = item => ({
  ...item,
  width: clampSize(Number(item.width) || MIN_ITEM_SIZE),
  height: clampSize(Number(item.height) || MIN_ITEM_SIZE),
  scale: clampScale(Number(item.scale) || 100),
  rotation: normalizeRotation(Number(item.rotation) || 0),
});

// Seçili bileşenin üzerinde çizilen tutamakların EKRAN pikseli cinsinden
// boyutları. Edit modunda svg viewBox'ı 1:1 piksele denk geldiği için yerel
// birime çevirmek `px / (scale/100)` kadar basit.
const HANDLE_PX = 7;
const ROTATE_GAP_PX = 26;

// Fare ile döndürürken Shift basılıysa bu adıma yuvarlanır.
const ROTATION_SNAP_DEG = 15;

// Tutamağın merkeze göre yönü (derece, 0 = yukarı) -> imleç seçimi için.
const RESIZE_HANDLES = [
  { key: 'nw', fx: 0, fy: 0, angle: 315 },
  { key: 'n', fx: 0.5, fy: 0, angle: 0 },
  { key: 'ne', fx: 1, fy: 0, angle: 45 },
  { key: 'e', fx: 1, fy: 0.5, angle: 90 },
  { key: 'se', fx: 1, fy: 1, angle: 135 },
  { key: 's', fx: 0.5, fy: 1, angle: 180 },
  { key: 'sw', fx: 0, fy: 1, angle: 225 },
  { key: 'w', fx: 0, fy: 0.5, angle: 270 },
];

const RESIZE_CURSORS = ['ns', 'nesw', 'ew', 'nwse'];
const resizeCursorFor = (handleAngle, rotation) =>
  `${RESIZE_CURSORS[Math.round((((handleAngle + rotation) % 360) + 360) % 360 / 45) % 4]}-resize`;

const FlowComponent = ({ mode = 'edit' }) => {
  const [isToolboxOpen, setIsToolboxOpen] = useState(true);
  const { id: flowId } = useParams();
  const receivedData = useSignalR(flowId && mode === 'view' ? flowId : null);
  const [droppedItems, setDroppedItems] = useState([]);
  const [draggingItemId, setDraggingItemId] = useState(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [isEditingConnector, setIsEditingConnector] = useState(false);
  const toast = useToast();
  const [isTextEditModalOpen, setIsTextEditModalOpen] = useState(false);
  const [currentEditingText, setCurrentEditingText] = useState('');
  const [currentEditingId, setCurrentEditingId] = useState(null);
  const [viewScale, setViewScale] = useState(1);
  const [allFlows, setAllFlows] = useState([]);
  const [isFlowAssignModalOpen, setIsFlowAssignModalOpen] = useState(false);
  const [isAssignModal2Open, setIsAssignModal2Open] = useState(false);
  const [selectedTag1, setSelectedTag1] = useState('');
  const [fontSize, setFontSize] = useState(16);
  const [textColor, setTextColor] = useState('#000');
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [borderColor, setBorderColor] = useState('#000000');
  const [dashed, setDashed] = useState(false);
  const navigate = useNavigate();
  const [alignmentHelper, setAlignmentHelper] = useState({
    visible: false,
    x: 0,
    y: 0,
  });
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    targetId: null,
  });

  // Toolbox arama kutusu (palette filtresi).
  const [toolboxQuery, setToolboxQuery] = useState('');
  // Context-menu sayısal alanlarının ham metni. Değer item'a yazılırken
  // clamp'leniyor ama kullanıcı yazarken kutuyu geçici olarak boşaltabilsin
  // diye taslak metin ayrı tutuluyor.
  const [sizeDraft, setSizeDraft] = useState({});
  // Aktif tutamak sürüklemesinin verisi (rotate/resize). State değil ref,
  // çünkü mousemove sırasında her karede okunuyor.
  const handleDragRef = useRef(null);
  // Sürükleme bitince window dinleyicilerini sökecek fonksiyon.
  const detachHandleDragRef = useRef(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [tagList, setTagList] = useState([]);
  const [currentItem, setCurrentItem] = useState(null);
  const diagramRef = useRef(null);

  const GROUPS_WITH_TWO_BITS = [
    '120',
    '121',
    '122',
    '123',
    '128',
    '129',
    '130',
    '131',
    '132',
    '133',
    '134',
    '135',
    '142',
  ];

  const [flowName, setFlowName] = useState('');
  const gridSize = 1.8;
  // const SNAP_THRESHOLD = 15;
  //
  // const snapToNeighbors = (dragId, rawX, rawY, items) => {
  //   const dragItem = items.find(i => i.id === dragId);
  //   if (!dragItem) return { x: rawX, y: rawY };
  //   const portSnap = findClosestPortSnap(
  //     { ...dragItem, x: rawX, y: rawY }, rawX, rawY, items, scaleFactor, SNAP_THRESHOLD
  //   );
  //   if (portSnap) return portSnap;
  //   const s = (dragItem.scale || 100) / 100;
  //   const sf = scaleFactor * 0.5;
  //   const w = (dragItem.width || 50) * s * sf;
  //   const h = (dragItem.height || 50) * s * sf;
  //   const dragEdges = { left: rawX, right: rawX + w, top: rawY, bottom: rawY + h };
  //   let bestX = rawX, bestY = rawY;
  //   let closestDx = SNAP_THRESHOLD + 1, closestDy = SNAP_THRESHOLD + 1;
  //   for (const other of items) {
  //     if (other.id === dragId) continue;
  //     const os = (other.scale || 100) / 100;
  //     const ow = (other.width || 50) * os * sf;
  //     const oh = (other.height || 50) * os * sf;
  //     const ob = { left: other.x, right: other.x + ow, top: other.y, bottom: other.y + oh };
  //     for (const { drag, target, adjust } of [
  //       { drag: dragEdges.right, target: ob.left, adjust: -w },
  //       { drag: dragEdges.left, target: ob.right, adjust: 0 },
  //       { drag: dragEdges.left, target: ob.left, adjust: 0 },
  //       { drag: dragEdges.right, target: ob.right, adjust: -w },
  //     ]) { const dx = Math.abs(drag - target); if (dx < closestDx) { closestDx = dx; bestX = target + adjust; } }
  //     for (const { drag, target, adjust } of [
  //       { drag: dragEdges.bottom, target: ob.top, adjust: -h },
  //       { drag: dragEdges.top, target: ob.bottom, adjust: 0 },
  //       { drag: dragEdges.top, target: ob.top, adjust: 0 },
  //       { drag: dragEdges.bottom, target: ob.bottom, adjust: -h },
  //     ]) { const dy = Math.abs(drag - target); if (dy < closestDy) { closestDy = dy; bestY = target + adjust; } }
  //   }
  //   return {
  //     x: closestDx <= SNAP_THRESHOLD ? bestX : rawX,
  //     y: closestDy <= SNAP_THRESHOLD ? bestY : rawY,
  //   };
  // };
  const getImagePathByToolboxId = (
    toolboxId,
    tagValue,
    mode,
    tagValue1 = null
  ) => {
    if (mode !== 'view') {
      const toolboxItem = toolboxItems.find(
        tool => String(tool.id) === String(toolboxId)
      );
      return toolboxItem ? toolboxItem.imagePath : '/images/default.png';
    }

    const toolboxItem = toolboxItems.find(
      tool => String(tool.id) === String(toolboxId)
    );

    if (!toolboxItem) {
      console.warn(`❌ ToolboxItem bulunamadı: toolboxId=${toolboxId}`);
      return '/images/default.png';
    }
    const groupId = toolboxItem.groupid;

    const groupItems = toolboxItems.filter(tool => tool.groupid === groupId);

    if (!groupItems.length) {
      console.warn(`❌ Grup içeriği bulunamadı: groupId=${groupId}`);
      return '/images/default.png';
    }

    const normalizeBool = val => {
      if (val == null || val === '') return NaN;
      if (val === true || val === 'true' || val === 'True') return 1;
      if (val === false || val === 'false' || val === 'False') return 0;
      const num = Number(val);
      return num;
    };

    let numericTagValue = normalizeBool(tagValue);

    // Henüz veri gelmemişse varsayılan görseli döndür
    if (isNaN(numericTagValue)) {
      const fallback =
        groupItems.find(tool => tool.view === '1') ?? groupItems[0];
      return fallback?.imagePath ?? '/images/default.png';
    }

    if (GROUPS_WITH_TWO_BITS.includes(groupId)) {
      const v1 = normalizeBool(tagValue) === 1 ? 1 : 0;
      const v2 = normalizeBool(tagValue1) === 1 ? 1 : 0;

      let colourType = 0;
      if (v1 === 1 && v2 === 0) colourType = 1;
      else if (v1 === 0 && v2 === 1) colourType = 2;
      else if (v1 === 0 && v2 === 0) colourType = 3;
      else if (v1 === 1 && v2 === 1) colourType = 4;

      const match = groupItems.find(
        tool => parseInt(tool.colourType) === colourType
      );
      if (match) return match.imagePath;
      return '/images/default.png';
    }
    // Range-based matching (minValue/maxValue)
    const hasRangeItems = groupItems.some(
      tool => tool.minValue != null && tool.maxValue != null
    );

    if (hasRangeItems) {
      const rangeMatch = groupItems.find(
        tool =>
          tool.minValue != null &&
          tool.maxValue != null &&
          numericTagValue >= tool.minValue &&
          numericTagValue <= tool.maxValue
      );
      if (rangeMatch) return rangeMatch.imagePath;

      // Fallback: view:'1' item or first item in group
      const fallback =
        groupItems.find(tool => tool.view === '1') ?? groupItems[0];
      return fallback?.imagePath ?? '/images/default.png';
    }

    // colourType-based matching
    let selectedToolboxItem = groupItems.find(
      tool => parseInt(tool.colourType, 10) === numericTagValue
    );
    if (!selectedToolboxItem) {
      selectedToolboxItem = groupItems.find(
        tool => parseInt(tool.colourType, 10) === 0
      );

      if (!selectedToolboxItem) {
        console.warn(
          `❌ Eşleşen toolbox bulunamadı: toolboxId=${toolboxId}, groupId=${groupId}, colourType=${numericTagValue}`
        );
      }
    }

    return selectedToolboxItem?.imagePath ?? '/images/default.png';
  };

  const prevIdRef = useRef(null);

  useEffect(() => {
    if (mode === 'view') return;
    const fetchAllFlows = async () => {
      try {
        const result = await fetchFlows();
        setAllFlows(result);
      } catch (err) {
        console.error('Flow listesi alınamadı:', err);
      }
    };

    fetchAllFlows();
  }, [mode]);

  useEffect(() => {
    if (mode !== 'view' || !receivedData) return;

    let parsedData = receivedData;

    if (typeof receivedData === 'string') {
      console.warn('🟡 receivedData STRING olarak geldi. Parsing yapılıyor...');
      try {
        parsedData = JSON.parse(receivedData);
      } catch (error) {
        console.error(
          '❌ JSON Parse Hatası! Geçersiz JSON formatı:',
          error,
          receivedData
        );
        return;
      }
    }

    if (!parsedData || typeof parsedData !== 'object') {
      console.error(
        '❌ receivedData HATALI! (Geçersiz JSON veya NULL)',
        parsedData
      );
      return;
    }

    if (!parsedData.hasOwnProperty('items')) {
      console.error('🚨 receivedData.items TANIMLANMAMIŞ!', parsedData);
      return;
    }

    let itemsArray = parsedData.items;

    if (typeof itemsArray === 'string') {
      console.warn(
        '🟡 receivedData.items STRING olarak geldi. Parsing yapılıyor...'
      );
      try {
        itemsArray = JSON.parse(itemsArray);
      } catch (error) {
        console.error('❌ JSON Parse Hatası (items):', error);
        return;
      }
    }

    if (!Array.isArray(itemsArray)) {
      console.error(
        '❌ receivedData.items YOK veya DİZİ DEĞİL! Type:',
        typeof itemsArray,
        'Value:',
        itemsArray
      );
      return;
    }

    const updatedItems = itemsArray
      .filter(item => item && item.id && item.toolboxId && item.position)
      .map(item => withSafeGeometry({
        id: item.id,
        toolboxId: item.toolboxId,
        textValue: item.textValue || '',
        x: item.position?.X || 0,
        y: item.position?.Y || 0,
        rotation: item.rotation ?? 0,
        tagId: item.tagId || null, // ⬅️ EKLE!
        tag1Id: item.tag1Id || null,
        tagInfo: item.tag ? { ...item.tag, registerId: item.tag.id } : null,
        tagInfo1: item.tag1 ? { ...item.tag1, registerId: item.tag1.id } : null,
        scale: item.scale ?? 100,
        width: item.width ?? 50,
        height: item.height ?? 50,
        linkedFlowId: item.linkedFlowId || null,
        linkedFlowName: item.linkedFlowName || null,
        fontSize: item.fontSize ?? 16,
        textColor: item.textColor ?? '#000000',
        backgroundColor: item.backgroundColor ?? '#ffffff',
        borderColor: item.borderColor ?? '#000000',
        isBold: item.isBold ?? false,
        isItalic: item.isItalic ?? false,
      }));

    setDroppedItems(updatedItems);
  }, [receivedData]);

  useEffect(() => {
    const fetchFlowData = async () => {
      if (!flowId || flowId === prevIdRef.current) return;
      prevIdRef.current = flowId;

      const normalizeItem = item => withSafeGeometry({
        ...item,
        fontSize: item.fontSize ?? 14,
        backgroundColor: item.backgroundColor ?? '#ffffff',
        borderColor: item.borderColor ?? '#000000',
        textColor: item.textColor ?? '#000000',
        isBold: item.isBold ?? false,
        isItalic: item.isItalic ?? false,
      });

      try {
        const flow = await getFlowById(flowId);
        const updatedItems = flow.items
          .filter(item => item && item.id && item.toolboxId && item.position)
          .map(item =>
            normalizeItem({
              ...item, // <-- tüm orijinal değerleri geçir
              id: item.id,
              toolboxId: item.toolboxId,
              x: item.position.x,
              y: item.position.y,
              rotation: item.rotation ?? 0,
              tagInfo: item.tag
                ? { ...item.tag, registerId: item.tag.id }
                : null,
              tagInfo1: item.tag1
                ? { ...item.tag1, registerId: item.tag1.id }
                : null,
              deviceInfo: item.deviceInfo || null,
              scale: item.scale ?? 100,
              width: item.width ?? 50,
              height: item.height ?? 50,
              textValue: item.textValue || '',
              linkedFlowId: item.linkedFlowId || null,
              linkedFlowName: item.linkedFlowName || null,
              tagId: item.tagId || null,
              tag1Id: item.tag1Id || null,
            })
          );

        setDroppedItems(updatedItems);
        setFlowName(flow.name);
      } catch (error) {
        console.error('❌ Error fetching flow data:', error);
      }
    };

    if (mode === 'edit') {
      fetchFlowData();
    }
  }, [flowId, mode]);

  const openSaveFlowModal = () => {
    setIsSaveModalOpen(true);
  };

  useEffect(() => {
    if (mode === 'view') return;
    const fetchTagList = async () => {
      try {
        const tags = await GetAllTags();
        setTagList(tags);
      } catch (error) {
        console.error('Error fetching tags:', error);
      }
    };
    fetchTagList();
  }, [mode]);

  useEffect(() => {
    if (!contextMenu.targetId) return;

    const found = droppedItems.find(x => x.id === contextMenu.targetId);
    if (found) {
      setCurrentItem(found);
    }
  }, [droppedItems, contextMenu.targetId]);

  useEffect(() => {
    const handleKeyDown = e => {
      if (
        isTextEditModalOpen ||
        contextMenu.visible ||
        isSaveModalOpen ||
        isAssignModalOpen
      )
        return;
      if (!selectedItemId) return;
      // Bir metin kutusuna (ör. toolbox arama kutusu) yazarken ok tuşları
      // imleci hareket ettirmeli, seçili bileşeni değil.
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable)
        return;
      // Only the arrow keys nudge the selected item — bail before
      // preventDefault() so typing anywhere else (e.g. the toolbox search
      // box) isn't swallowed while an item happens to be selected.
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key))
        return;

      const step = gridSize;
      e.preventDefault();
      let newX, newY;

      setDroppedItems(prevItems =>
        prevItems.map(item => {
          if (item.id === selectedItemId) {
            switch (e.key) {
              case 'ArrowUp':
                newX = item.x;
                newY = item.y - step;
                break;
              case 'ArrowDown':
                newX = item.x;
                newY = item.y + step;
                break;
              case 'ArrowLeft':
                newX = item.x - step;
                newY = item.y;
                break;
              case 'ArrowRight':
                newX = item.x + step;
                newY = item.y;
                break;
              default:
                return item;
            }

            setAlignmentHelper({
              visible: true,
              x: newX,
              y: newY,
            });

            return { ...item, x: newX, y: newY };
          }
          return item;
        })
      );
    };

    const handleKeyUp = () => {
      setAlignmentHelper({ visible: false, x: 0, y: 0 });
    };

    if (!contextMenu.visible && !isSaveModalOpen && !isAssignModalOpen) {
      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('keyup', handleKeyUp);
    } else {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [
    selectedItemId,
    gridSize,
    isTextEditModalOpen,
    contextMenu.visible,
    isSaveModalOpen,
    isAssignModalOpen,
  ]);

  const handleDragStart = (e, item) => {
    e.dataTransfer.setData('type', item.type);
    e.dataTransfer.setData('imagePath', item.imagePath);
    e.dataTransfer.setData('toolboxId', item.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = async e => {
    if (mode === 'view') return;
    e.preventDefault();

    if (!diagramRef.current) {
      console.warn('⚠️ diagramRef.current is null, drop işlemi iptal edildi.');
      return;
    }

    const type = e.dataTransfer.getData('type');
    const imagePath = e.dataTransfer.getData('imagePath');
    const toolboxId = e.dataTransfer.getData('toolboxId');

    const { x, y } = getMousePositionRelativeToSvg(e);
    const toolboxItem = toolboxItems.find(
      item => String(item.id) === String(toolboxId)
    );

    if (!toolboxItem) {
      console.warn(`⚠️ Toolbox item bulunamadı: ${toolboxId}`);
      return;
    }

    const { width, height } = await getImageSize(toolboxItem.imagePath);

    setDroppedItems(prevItems => {
      const newItem = withSafeGeometry({
        id: uuidv4(),
        toolboxId,
        type,
        textValue: toolboxId === '200' ? '' : '',
        imagePath,
        x,
        y,
        rotation: 0,
        scale: 100,
        width,
        height,
      });

      return type === 'Pipe'
        ? [newItem, ...prevItems]
        : [...prevItems, newItem];
    });
  };

  const handleMouseDownOnItem = (e, item) => {
    if (mode === 'view') return;
    e.stopPropagation();
    e.preventDefault();
    window.getSelection()?.removeAllRanges();
    const { x, y } = getMousePositionRelativeToSvg(e);
    dragOffsetRef.current = { x: x - item.x, y: y - item.y };
    setSelectedItemId(item.id);
    setDraggingItemId(item.id);
  };

  const handleMouseMoveOnDiagram = e => {
    if (mode === 'view') return;
    if (!draggingItemId) return;
    e.preventDefault();

    const { x, y } = getMousePositionRelativeToSvg(e);
    const offsetX = x - dragOffsetRef.current.x;
    const offsetY = y - dragOffsetRef.current.y;

    const snappedX = Math.round(offsetX / gridSize) * gridSize;
    const snappedY = Math.round(offsetY / gridSize) * gridSize;

    setAlignmentHelper({ visible: true, x: snappedX, y: snappedY });

    setDroppedItems(prevItems =>
      prevItems.map(item =>
        item.id === draggingItemId
          ? { ...item, x: snappedX, y: snappedY }
          : item
      )
    );
  };

  const handleMouseUpOnDiagram = () => {
    if (mode === 'view') return;

    setDraggingItemId(null);
    setAlignmentHelper({ visible: false, x: 0, y: 0 });
  };

  const getMousePositionRelativeToSvg = event => {
    const svg = diagramRef.current.querySelector('svg');
    const pt = svg.createSVGPoint();

    pt.x = event.clientX;
    pt.y = event.clientY;

    const transformedPoint = pt.matrixTransform(svg.getScreenCTM().inverse());
    return {
      x: transformedPoint.x,
      y: transformedPoint.y,
    };
  };

  const getScreenCoordsOfSvgItem = (x, y) => {
    const svg = diagramRef.current.querySelector('svg');
    if (!svg) return { x: 0, y: 0 };

    const pt = svg.createSVGPoint();
    pt.x = x;
    pt.y = y;

    const globalPoint = pt.matrixTransform(svg.getScreenCTM());
    return {
      x: globalPoint.x,
      y: globalPoint.y,
    };
  };

  const handleContextMenu = (e, item) => {
    if (mode === 'view') return;

    e.preventDefault();

    const screenPos = getScreenCoordsOfSvgItem(item.x, item.y);

    setContextMenu({
      visible: true,
      x: screenPos.x + 20,
      y: screenPos.y,
      targetId: item.id,
    });

    setCurrentItem(item);
  };

  const handleCloseContextMenu = () => {
    setContextMenu({ visible: false, x: 0, y: 0, targetId: null });
  };

  // Context-menu sayısal alanları (Scale / Width / Height / Rotation).
  // Kullanıcı yazarken kutuyu geçici olarak boşaltabilsin diye ham metin
  // `sizeDraft`te tutuluyor; item'a yazılan değer ise HER ZAMAN clamp'li —
  // böylece 0 girilip bileşenin ekrandan kaybolması mümkün değil.
  const commitNumericField = (field, rawValue) => {
    if (mode === 'view') return;
    setSizeDraft(prev => ({ ...prev, [field]: rawValue }));

    const parsed = parseInt(rawValue, 10);
    if (Number.isNaN(parsed)) return; // kutu boş bırakıldı — item'a dokunma

    setDroppedItems(prevItems =>
      prevItems.map(item => {
        if (item.id !== contextMenu.targetId) return item;
        let value;
        if (field === 'rotation') {
          value = normalizeRotation(parsed);
        } else if (field === 'scale') {
          value = Math.max(clampScale(parsed), minScaleFor(item));
        } else {
          value = Math.max(clampSize(parsed), minRawSizeFor(item));
        }
        return { ...item, [field]: value };
      })
    );
  };

  // Odak çıkınca taslak metni gerçek (clamp'lenmiş) değere geri çek, böylece
  // "0" yazıp odaktan çıkan kullanıcı kutuda 10 görür.
  const syncNumericField = field => {
    const item = droppedItems.find(i => i.id === contextMenu.targetId);
    setSizeDraft(prev => ({ ...prev, [field]: String(item?.[field] ?? '') }));
  };

  const numericFieldValue = (field, fallback) => {
    if (sizeDraft[field] !== undefined) return sizeDraft[field];
    const item = droppedItems.find(i => i.id === contextMenu.targetId);
    return String(item?.[field] ?? fallback);
  };

  // Hedef bileşen değişince taslakları sıfırla.
  useEffect(() => {
    setSizeDraft({});
  }, [contextMenu.targetId]);

  const handleDelete = () => {
    setDroppedItems(prevItems =>
      prevItems.filter(item => item.id !== contextMenu.targetId)
    );
    setContextMenu({ visible: false, x: 0, y: 0, targetId: null });
  };

  const openAssignRegisterModal = () => {
    setIsAssignModalOpen(true);
  };

  const getImageSize = imagePath => {
    return new Promise(resolve => {
      const img = new Image();
      // Görsel yüklenemezse promise asla çözülmezdi ve drop sessizce
      // yarıda kalırdı; makul bir varsayılana düşüyoruz.
      img.onerror = () => resolve({ width: 50, height: 50 });
      img.onload = () => {
        resolve({
          width: img.naturalWidth || 50,
          height: img.naturalHeight || 50,
        });
      };
      img.src = imagePath;
    });
  };

  const [svgSize, setSvgSize] = useState({ width: 0, height: 0 });

  const [scaleFactor, setScaleFactors] = useState(1);

  // -------------------------------------------------------------------------
  // Geometri yardımcıları
  // -------------------------------------------------------------------------
  // Canvas'ta bir bileşen şu transform ile çiziliyor:
  //   translate(x, y) scale(scale/100) rotate(rotation, cx, cy)
  // `getItemBox` bu transform'dan ÖNCEKİ (yerel) kutuyu SVG birimi cinsinden
  // verir. Üç ayrı ölçek kuralı var, çünkü mevcut çizim kodu böyle:
  //   - Text Box / Display Text : width/height doğrudan SVG birimi
  //   - Connector (line/arrow)  : uzunluk = width, yükseklik yok, pivot orijin
  //   - Diğer her şey (<image>) : width * scaleFactor * 0.5
  const IMAGE_SIZE_UNIT = 0.5;

  const getToolboxMeta = toolboxId =>
    toolboxItems.find(t => String(t.id) === String(toolboxId));

  // item.width -> yerel SVG birimi çarpanı
  const getSizeUnit = item => {
    if (item.toolboxId === '100' || item.toolboxId === '200') return 1;
    if (getToolboxMeta(item.toolboxId)?.isConnector) return 1;
    return scaleFactor * IMAGE_SIZE_UNIT;
  };

  // Bileşenin mevcut ölçeğinde, ekranda MIN_RENDERED_PX pikselin altına
  // düşmeyecek en küçük ham width/height.
  const minRawSizeFor = item => {
    const unit = getSizeUnit(item) || 1;
    const s = (Number(item.scale) || 100) / 100;
    return Math.max(MIN_ITEM_SIZE, Math.ceil(MIN_RENDERED_PX / (unit * s)));
  };

  // Bileşenin mevcut boyutunda, aynı tabanı koruyan en küçük ölçek (%).
  const minScaleFor = item => {
    const unit = getSizeUnit(item) || 1;
    const shortSide =
      Math.min(Number(item.width) || 0, Number(item.height) || 0) * unit;
    if (!shortSide) return MIN_ITEM_SCALE;
    return Math.max(
      MIN_ITEM_SCALE,
      Math.min(MAX_ITEM_SCALE, Math.ceil((MIN_RENDERED_PX / shortSide) * 100))
    );
  };

  const getItemBox = item => {
    const unit = getSizeUnit(item);
    const w = (Number(item.width) || 0) * unit;
    const h = (Number(item.height) || 0) * unit;
    if (getToolboxMeta(item.toolboxId)?.isConnector) return { w, h: 0 };
    return { w, h };
  };

  // Tek kaynak: hem renderItem hem pipes-layer hem de seçim tutamakları
  // bu transform'u kullanıyor, aksi hâlde tutamaklar bileşenle hizasız kalır.
  //
  // ÖNEMLİ: döndürme artık kutunun MERKEZİ etrafında (`rotate(deg, cx, cy)`).
  // Eskiden `rotate(deg)` sol-üst köşe etrafında dönüyordu; rotation'ı
  // ayarlayan bir arayüz olmadığı için kayıtlı tüm bileşenlerde rotation=0
  // ve rotate(0) her pivotta aynı sonucu verdiğinden bu değişiklik mevcut
  // ekranların görünümünü etkilemez.
  const getItemTransform = item => {
    const s = (Number(item.scale) || 100) / 100;
    const rotation = Number(item.rotation) || 0;
    const meta = getToolboxMeta(item.toolboxId);
    if (meta?.isConnector) {
      // Connector'lar başlangıç noktası etrafında dönüyor (baseAngle ile aynı
      // pivot) — kayıtlı ok/çizgilerin yeri kaymasın diye korundu.
      return `translate(${item.x}, ${item.y}) scale(${s}) rotate(${
        (meta.baseAngle || 0) + rotation
      })`;
    }
    const { w, h } = getItemBox(item);
    return `translate(${item.x}, ${item.y}) scale(${s}) rotate(${rotation}, ${
      w / 2
    }, ${h / 2})`;
  };

  const updateItem = (id, patch) =>
    setDroppedItems(prevItems =>
      prevItems.map(item => (item.id === id ? { ...item, ...patch } : item))
    );

  const setItemRotation = deg => {
    const id = contextMenu.targetId || selectedItemId;
    if (!id || mode === 'view') return;
    updateItem(id, { rotation: normalizeRotation(deg) });
    setSizeDraft(prev => ({ ...prev, rotation: undefined }));
  };

  const rotateSelectedBy = delta => {
    const id = contextMenu.targetId || selectedItemId;
    if (!id || mode === 'view') return;
    const item = droppedItems.find(i => i.id === id);
    if (!item) return;
    updateItem(id, {
      rotation: normalizeRotation((Number(item.rotation) || 0) + delta),
    });
    setSizeDraft(prev => ({ ...prev, rotation: undefined }));
  };

  // -------------------------------------------------------------------------
  // Tutamak sürükleme (döndürme + kenardan boyutlandırma)
  // -------------------------------------------------------------------------
  // NOT: window dinleyicileri burada, mousedown ile AYNI tick'te bağlanıyor.
  // useEffect ile bağlansaydı passive effect flush'ı beklenirdi ve hızlı bir
  // mousedown+mousemove ikilisinde ilk hareketler kaçabilirdi.
  const beginHandleDrag = (e, item, kind, handle) => {
    if (mode === 'view') return;
    e.stopPropagation();
    e.preventDefault();
    window.getSelection()?.removeAllRanges();

    const meta = getToolboxMeta(item.toolboxId);

    handleDragRef.current = {
      kind,
      handle,
      id: item.id,
      isConnector: !!meta?.isConnector,
      baseAngle: meta?.baseAngle || 0,
      box: getItemBox(item),
      unit: getSizeUnit(item),
      scale: (Number(item.scale) || 100) / 100,
      rotation: Number(item.rotation) || 0,
      origin: { x: item.x, y: item.y },
      shiftKey: e.shiftKey,
    };

    setSelectedItemId(item.id);

    const onMove = ev => {
      const drag = handleDragRef.current;
      if (!drag || !diagramRef.current) return;
      drag.shiftKey = ev.shiftKey;
      const mouse = getMousePositionRelativeToSvg(ev);
      if (drag.kind === 'rotate') applyRotateDrag(drag, mouse);
      else applyResizeDrag(drag, mouse);
    };
    const detach = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    function onUp() {
      detach();
      detachHandleDragRef.current = null;
      handleDragRef.current = null;
      setSizeDraft({});
    }

    detachHandleDragRef.current?.();
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    detachHandleDragRef.current = detach;
  };

  const applyRotateDrag = (drag, mouse) => {
    // Döndürme merkezi: normal bileşenlerde kutunun merkezi, connector'da
    // başlangıç noktası (transform'daki pivot ile aynı olmak zorunda).
    const center = drag.isConnector
      ? drag.origin
      : {
          x: drag.origin.x + (drag.scale * drag.box.w) / 2,
          y: drag.origin.y + (drag.scale * drag.box.h) / 2,
        };

    const pointerDeg =
      (Math.atan2(mouse.y - center.y, mouse.x - center.x) * 180) / Math.PI;

    // Tutamağın rotation=0 iken bulunduğu yön: normal bileşende yukarı
    // (-90°), connector'da çizginin ucu (baseAngle yönü).
    let deg = drag.isConnector
      ? pointerDeg - drag.baseAngle
      : pointerDeg + 90;

    if (drag.shiftKey) {
      deg = Math.round(deg / ROTATION_SNAP_DEG) * ROTATION_SNAP_DEG;
    }
    updateItem(drag.id, { rotation: normalizeRotation(deg) });
  };

  const applyResizeDrag = (drag, mouse) => {
    const { box, scale: s, unit, rotation: th, origin } = drag;

    // --- Connector: sadece uzunluk. Fareyi çizginin yönüne izdüşürüyoruz.
    if (drag.isConnector) {
      const ang = ((drag.baseAngle + th) * Math.PI) / 180;
      const dx = (mouse.x - origin.x) / s;
      const dy = (mouse.y - origin.y) / s;
      const len = dx * Math.cos(ang) + dy * Math.sin(ang);
      updateItem(drag.id, { width: clampSize(len / unit) });
      return;
    }

    // --- Diğer bileşenler ---------------------------------------------------
    // 1) Fare noktasını, bileşenin DÖNDÜRÜLMEMİŞ yerel koordinatına taşı.
    const c0 = { x: box.w / 2, y: box.h / 2 };
    const rad = (-th * Math.PI) / 180;
    const ux = (mouse.x - origin.x) / s - c0.x;
    const uy = (mouse.y - origin.y) / s - c0.y;
    const m = {
      x: c0.x + ux * Math.cos(rad) - uy * Math.sin(rad),
      y: c0.y + ux * Math.sin(rad) + uy * Math.cos(rad),
    };

    // 2) Tutamağın karşısındaki kenar sabit kalacak şekilde yeni kutu.
    const hx = drag.handle.includes('w')
      ? 'w'
      : drag.handle.includes('e')
        ? 'e'
        : null;
    const hy = drag.handle.includes('n')
      ? 'n'
      : drag.handle.includes('s')
        ? 's'
        : null;

    // Yerel birim x s = ekran pikseli; ikisinin de tabanını uyguluyoruz.
    const minLocal = Math.max(MIN_ITEM_SIZE * unit, MIN_RENDERED_PX / s);
    const maxLocal = MAX_ITEM_SIZE * unit;
    const clampLocal = v => Math.min(Math.max(v, minLocal), maxLocal);

    let w = box.w;
    let h = box.h;
    if (hx === 'w') w = clampLocal(box.w - m.x);
    else if (hx === 'e') w = clampLocal(m.x);
    if (hy === 'n') h = clampLocal(box.h - m.y);
    else if (hy === 's') h = clampLocal(m.y);

    // 3) Köşe tutamağı + Shift => en/boy oranını koru.
    if (hx && hy && drag.shiftKey && box.w > 0 && box.h > 0) {
      const ratio = box.w / box.h;
      if (w / h > ratio) w = clampLocal(h * ratio);
      else h = clampLocal(w / ratio);
    }

    // 4) Backend int istiyor: önce ham birime çevirip yuvarla, sonra yerleşimi
    //    YUVARLANMIŞ boyuta göre hesapla ki sabit kenar gerçekten sabit kalsın.
    const nextWidth = clampSize(w / unit);
    const nextHeight = clampSize(h / unit);
    const fw = nextWidth * unit;
    const fh = nextHeight * unit;

    // 5) Yeni kutunun merkezini ESKİ yerel çerçevede bul, eski transform'la
    //    ekrana taşı, oradan yeni origin'i geri hesapla. Böylece hem sabit
    //    kenar yerinde kalır hem de dönme açısı değişmez.
    const left = hx === 'w' ? box.w - fw : 0;
    const top = hy === 'n' ? box.h - fh : 0;
    const cx = left + fw / 2;
    const cy = top + fh / 2;

    const thr = (th * Math.PI) / 180;
    const vx = cx - c0.x;
    const vy = cy - c0.y;
    const rx = vx * Math.cos(thr) - vy * Math.sin(thr);
    const ry = vx * Math.sin(thr) + vy * Math.cos(thr);

    updateItem(drag.id, {
      x: origin.x + s * (c0.x + rx) - (s * fw) / 2,
      y: origin.y + s * (c0.y + ry) - (s * fh) / 2,
      width: nextWidth,
      height: nextHeight,
    });
  };

  // Bileşen sökülürken yarım kalmış bir sürüklemenin dinleyicileri asılı
  // kalmasın.
  useEffect(() => () => detachHandleDragRef.current?.(), []);

  const renderItem = (item, isEditable = false) => {
    if (
      [
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '201',
        '202',
        '203',
        '204',
      ].includes(item.toolboxId)
    ) {
      return null;
    }

    // Konum/ölçek/açı artık getItemTransform() üzerinden tek yerden
    // hesaplanıyor; burada sadece çizim içeriğini ilgilendiren alanlar var.
    const {
      id,
      toolboxId,
      width = 150,
      height = 150,
      fontSize = 14,
      textColor = '#000000',
      backgroundColor = '#ffffff',
      borderColor = '#000000',
      isBold = false,
      isItalic = false,
      textValue = '',
      tagInfo,
      linkedFlowName,
    } = item;

    const transform = getItemTransform(item);
    if (toolboxId === '100') {
      // Display Text
      return (
        <g
          key={id}
          transform={transform}
          style={{
            cursor: isEditable ? 'text' : 'grab',
            outline: item.id === selectedItemId ? '0.5px solid black' : 'none',
          }}
          onMouseDown={e => handleMouseDownOnItem(e, item)}
          onContextMenu={e => handleContextMenu(e, item)}
        >
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill={backgroundColor}
            stroke={borderColor}
            strokeWidth={2}
            rx={6}
          />
          <text
            x={width / 2}
            y={height / 2}
            fill={textColor}
            fontSize={fontSize * 0.7}
            fontWeight={isBold ? 'bold' : 'normal'}
            fontStyle={isItalic ? 'italic' : 'normal'}
            dominantBaseline="middle"
            textAnchor="middle"
            pointerEvents={isEditable ? 'auto' : 'none'}
          >
            {tagInfo?.value ? Number(tagInfo.value).toFixed(2) : ''}
          </text>
        </g>
      );
    }

    if (toolboxId === '200') {
      return (
        <g
          key={id}
          transform={transform}
          onDoubleClick={e => handleDoubleClick(e, item)}
          style={{
            cursor: isEditable ? 'text' : 'grab',
            outline: item.id === selectedItemId ? '0.5px solid black' : 'none',
          }}
          onMouseDown={e => handleMouseDownOnItem(e, item)}
          onContextMenu={e => handleContextMenu(e, item)}
        >
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill={backgroundColor}
            stroke={borderColor}
            strokeWidth={2}
            rx={6}
          />
          <text
            x={width / 2}
            y={height / 2}
            fill={textColor}
            fontSize={fontSize}
            fontWeight={isBold ? 'bold' : 'normal'}
            fontStyle={isItalic ? 'italic' : 'normal'}
            dominantBaseline="middle"
            textAnchor="middle"
            pointerEvents={isEditable ? 'auto' : 'none'}
          >
            {textValue || 'Click to edit'}
          </text>
        </g>
      );
    }

    if (toolboxId === '9999') {
      return (
        <g
          key={id}
          transform={transform}
          onMouseDown={e => handleMouseDownOnItem(e, item)}
          onContextMenu={e => handleContextMenu(e, item)}
          onClick={e => {
            e.stopPropagation();
            if (mode !== 'view') return;
            if (item.toolboxId === '9999' && item.linkedFlowId) {
              navigate(`/flow/view/${item.linkedFlowId}`); // SAME TAB
            }
          }}
          style={{
            cursor: 'pointer',
            outline: item.id === selectedItemId ? '0.5px solid black' : 'none',
          }}
        >
          <image
            href={getImagePathByToolboxId(
              item.toolboxId,
              item.tagInfo?.value,
              mode,
              item.tagInfo1?.value
            )}
            width={item.width * scaleFactor * 0.5}
            height={item.height * scaleFactor * 0.5}
            x="0"
            y="0"
            preserveAspectRatio="none"
            style={{
              cursor: mode === 'view' ? 'pointer' : 'default',
              pointerEvents: 'auto', // 🔥 bu şart
            }}
            onClick={e => {
              e.stopPropagation();
              if (mode !== 'view') return;
              if (item.toolboxId === '9999' && item.linkedFlowId) {
                navigate(`/flow/view/${item.linkedFlowId}`);
              }
            }}
          />
          <text
            x={(item.width * scaleFactor * 0.5) / 2}
            y={(item.height * scaleFactor * 0.5) / 2}
            fontSize="14"
            fill="white"
            textAnchor="middle"
            dominantBaseline="middle"
            fontFamily="Arial"
            pointerEvents="none"
          >
            {linkedFlowName || 'Flow'}
          </text>
        </g>
      );
    }

    const toolboxMeta = toolboxItems.find(
      t => String(t.id) === String(toolboxId)
    );
    if (toolboxMeta?.isConnector) {
      // Plain line / dashed direction arrow — drawn as native SVG (not an
      // <image>) so `borderColor` (edited via double-click, same modal as
      // Text Box) actually recolors it live. Drawn pointing right/horizontal
      // in local coords; connectorKind + baseAngle rotate it into the preset
      // direction, then the item's own rotation (fare ile ya da context
      // menüden) fine-tunes it further — bkz. getItemTransform().
      const len = width;
      const isArrow = toolboxMeta.connectorKind === 'arrow';
      // Solid vs dashed is a per-instance toggle (edit modal "Kesikli"
      // checkbox), not a separate palette entry. Undefined (never toggled
      // yet) falls back to the old default: arrows dashed, lines solid.
      const isDashed = item.dashed ?? isArrow;
      return (
        <g
          key={id}
          transform={transform}
          onMouseDown={e => handleMouseDownOnItem(e, item)}
          onContextMenu={e => handleContextMenu(e, item)}
          onDoubleClick={e => handleDoubleClick(e, item)}
          style={{
            cursor: 'grab',
            outline: item.id === selectedItemId ? '0.5px solid black' : 'none',
          }}
        >
          <line
            x1={0}
            y1={0}
            x2={isArrow ? len - 16 : len}
            y2={0}
            stroke={borderColor}
            strokeWidth={4}
            strokeDasharray={isDashed ? '10 7' : undefined}
            strokeLinecap="round"
          />
          {isArrow && (
            <polygon points={`${len},0 ${len - 16},-8 ${len - 16},8`} fill={borderColor} />
          )}
        </g>
      );
    }

    return (
      <g
        key={id}
        transform={transform}
        onMouseDown={e => handleMouseDownOnItem(e, item)}
        onContextMenu={e => handleContextMenu(e, item)}
        style={{
          cursor: 'grab',
          outline: item.id === selectedItemId ? '0.5px solid black' : 'none',
        }}
      >
        <image
          href={getImagePathByToolboxId(
            item.toolboxId,
            item.tagInfo?.value,
            mode,
            item.tagInfo1?.value
          )}
          width={item.width * scaleFactor * 0.5}
          height={item.height * scaleFactor * 0.5}
          x="0"
          y="0"
          preserveAspectRatio="none"
        />
        {toolboxId === '300' && (
          <foreignObject x="0" y="0" width={width} height={height}>
            <div
              xmlns="http://www.w3.org/1999/xhtml"
              style={{
                width: '100%',
                height: '100%',
                border: '1px solid gray',
                background: 'white',
                overflow: 'auto',
              }}
            >
              <AlarmsDisplayForFlowComponents />
            </div>
          </foreignObject>
        )}
      </g>
    );
  };

  // -------------------------------------------------------------------------
  // Seçim katmanı — döndürme + kenar/köşe tutamakları
  // -------------------------------------------------------------------------
  // Tutamaklar bileşenin KENDİ transform'u içinde çiziliyor; böylece bileşen
  // döndükçe/ölçeklendikçe tutamaklar da onunla birlikte hareket ediyor.
  // Edit modunda svg viewBox'ı 1:1 piksele denk geldiği için, `u = 1/scale`
  // çarpanı tutamakları bileşenin ölçeğinden bağımsız olarak sabit ekran
  // boyutunda tutar.
  const renderSelectionOverlay = () => {
    if (mode !== 'edit' || !selectedItemId) return null;
    const item = droppedItems.find(i => i.id === selectedItemId);
    if (!item) return null;

    const s = (Number(item.scale) || 100) / 100;
    const u = 1 / s;
    const box = getItemBox(item);
    const rotation = Number(item.rotation) || 0;
    const transform = getItemTransform(item);
    const meta = getToolboxMeta(item.toolboxId);
    const half = (HANDLE_PX * u) / 2;
    const gap = ROTATE_GAP_PX * u;
    const accent = '#2b6cb0';

    const handleStyle = {
      fill: '#ffffff',
      stroke: accent,
      strokeWidth: 1.25 * u,
    };
    const outlineProps = {
      fill: 'none',
      stroke: accent,
      strokeWidth: u,
      strokeDasharray: `${4 * u} ${3 * u}`,
      pointerEvents: 'none',
    };

    // Connector (çizgi/ok): tek uzunluk tutamağı + ucunda döndürme tutamağı.
    if (meta?.isConnector) {
      return (
        <g transform={transform} pointerEvents="all">
          <line x1={0} y1={0} x2={box.w} y2={0} {...outlineProps} />
          <line
            x1={box.w}
            y1={0}
            x2={box.w + gap}
            y2={0}
            stroke={accent}
            strokeWidth={u}
            pointerEvents="none"
          />
          <circle
            cx={box.w + gap}
            cy={0}
            r={half * 1.3}
            {...handleStyle}
            style={{ cursor: 'grab' }}
            onMouseDown={e => beginHandleDrag(e, item, 'rotate', 'rotate')}
          />
          <rect
            x={box.w - half}
            y={-half}
            width={half * 2}
            height={half * 2}
            {...handleStyle}
            style={{ cursor: 'ew-resize' }}
            onMouseDown={e => beginHandleDrag(e, item, 'resize', 'e')}
          />
        </g>
      );
    }

    return (
      <g transform={transform} pointerEvents="all">
        <rect x={0} y={0} width={box.w} height={box.h} {...outlineProps} />
        {/* döndürme kolu — kutunun üstünde */}
        <line
          x1={box.w / 2}
          y1={0}
          x2={box.w / 2}
          y2={-gap}
          stroke={accent}
          strokeWidth={u}
          pointerEvents="none"
        />
        <circle
          cx={box.w / 2}
          cy={-gap}
          r={half * 1.3}
          {...handleStyle}
          style={{ cursor: 'grab' }}
          onMouseDown={e => beginHandleDrag(e, item, 'rotate', 'rotate')}
        >
          <title>Sürükleyerek döndür (Shift = 15° adım)</title>
        </circle>
        {/* 4 köşe + 4 kenar tutamağı */}
        {RESIZE_HANDLES.map(h => (
          <rect
            key={h.key}
            x={box.w * h.fx - half}
            y={box.h * h.fy - half}
            width={half * 2}
            height={half * 2}
            {...handleStyle}
            style={{ cursor: resizeCursorFor(h.angle, rotation) }}
            onMouseDown={e => beginHandleDrag(e, item, 'resize', h.key)}
          />
        ))}
      </g>
    );
  };

  useEffect(() => {
    let timeoutId;
    const updateSize = () => {
      const navbar = document.getElementById('navbar');
      const sidebar = document.getElementById('sidebar');

      const navbarHeight = navbar?.offsetHeight || 50;
      const sidebarWidth = sidebar?.offsetWidth || 0;

      const newWidth =
        mode === 'view' ? window.innerWidth : window.innerWidth - sidebarWidth;
      const newHeight =
        mode === 'view'
          ? window.innerHeight
          : window.innerHeight - navbarHeight;

      const widthScale = newWidth / svgSize.width;
      const heightScale = newHeight / svgSize.height;

      const newScaleFactor = Math.min(widthScale, heightScale);
      setScaleFactors(newScaleFactor);

      setSvgSize(prevSize => {
        if (prevSize.width === newWidth && prevSize.height === newHeight) {
          return prevSize;
        }
        return { width: newWidth, height: newHeight };
      });
    };

    updateSize();
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateSize, 100);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [mode, svgSize]);

  const openFlowAssignmentModal = () => {
    setIsFlowAssignModalOpen(true);
  };

  useEffect(() => {
    if (mode === 'view') {
      const disableBrowserZoom = e => {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
        }
      };

      window.addEventListener('wheel', disableBrowserZoom, { passive: false });
      window.addEventListener('keydown', disableBrowserZoom, {
        passive: false,
      });

      return () => {
        window.removeEventListener('wheel', disableBrowserZoom);
        window.removeEventListener('keydown', disableBrowserZoom);
      };
    }
  }, [mode]);

  const handleDoubleClick = (e, item) => {
    if (mode === 'view') return;
    if (e && e.stopPropagation) e.stopPropagation();
    const toolboxMeta = toolboxItems.find(
      t => String(t.id) === String(item.toolboxId)
    );
    if (item.toolboxId === '200' || toolboxMeta?.isConnector) {
      setCurrentEditingText(item.textValue || '');
      setCurrentEditingId(item.id);
      setIsEditingConnector(!!toolboxMeta?.isConnector);
      setIsTextEditModalOpen(true);

      setFontSize(item.fontSize ?? 14);
      setTextColor(item.textColor ?? '#000000');
      setBackgroundColor(item.backgroundColor ?? '#ffffff');
      setBorderColor(item.borderColor ?? '#000000');
      setIsBold(item.isBold ?? false);
      setIsItalic(item.isItalic ?? false);
      setDashed(item.dashed ?? toolboxMeta?.connectorKind === 'arrow');
    }
  };

  const handleSaveTextEdit = ({
    text,
    fontSize,
    textColor,
    backgroundColor,
    borderColor,
    isBold,
    isItalic,
    dashed,
  }) => {
    setDroppedItems(prevItems =>
      prevItems.map(item =>
        item.id === currentEditingId
          ? {
              ...item,
              textValue: text,
              fontSize,
              textColor,
              backgroundColor,
              borderColor,
              isBold,
              isItalic,
              dashed,
            }
          : item
      )
    );

    setIsTextEditModalOpen(false);
  };

  useEffect(() => {
    document.documentElement.style.height = '100%';
    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';
    document.documentElement.style.overflow = 'hidden';

    document.body.style.height = '100%';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflow = 'hidden';

    return () => {
      document.documentElement.style.height = '';
      document.documentElement.style.margin = '';
      document.documentElement.style.padding = '';
      document.documentElement.style.overflow = '';

      document.body.style.height = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.body.style.overflow = '';
    };
  }, []);

  const canvasMinWidth = Math.max(svgSize.width || window.innerWidth, 2000);
  const canvasMinHeight = Math.max(svgSize.height || window.innerHeight, 2000);
  const maxX = Math.max(...droppedItems.map(i => i.x + i.width), canvasMinWidth);
  const maxY = Math.max(...droppedItems.map(i => i.y + i.height), canvasMinHeight);
  const isFullscreen = mode === 'view';

  // Palette (toolbox) listesi + arama filtresi.
  // Arama; görünen ad (name/type) ile birlikte id/groupid üzerinde de çalışır,
  // çünkü eski bileşenlerin adı hâlâ '021v01c01a01' gibi asset kodları.
  const paletteItems = useMemo(() => {
    const visible = toolboxItems.filter(item => item.view === '1');
    const query = toolboxQuery.trim().toLowerCase();
    if (!query) return visible;
    const terms = query.split(/\s+/);
    return visible.filter(item => {
      const haystack =
        `${item.name || ''} ${item.type || ''} ${item.id} ${item.groupid}`.toLowerCase();
      return terms.every(term => haystack.includes(term));
    });
  }, [toolboxQuery]);

  // helper: svg koordinatını konteyner pikseline çevir
  const getContainerCoordsFromSvg = (x, y) => {
    const container = diagramRef.current;
    const svg = container?.querySelector('svg');
    if (!container || !svg) return { left: 0, top: 0 };

    const pt = svg.createSVGPoint();
    pt.x = x;
    pt.y = y;

    const gp = pt.matrixTransform(svg.getScreenCTM()); // ekran pikseli
    const rect = container.getBoundingClientRect(); // container viewport
    return {
      left: gp.x - rect.left,
      top: gp.y - rect.top,
    };
  };

  return (
    <Flex flex="1" w="100%" h="100%" overflow="hidden">
      {mode === 'edit' && (
        <IconButton
          aria-label="Toggle toolbox"
          icon={isToolboxOpen ? <FiChevronLeft /> : <FiChevronRight />}
          size="sm"
          onClick={() => setIsToolboxOpen(v => !v)}
          position="absolute"
          top="12px"
          left="12px"
          zIndex={30} // panelden yüksek
          borderRadius="full"
          boxShadow="md"
          bg="black"
          color="white" // ikon rengi
          _hover={{ bg: 'gray.800' }}
          _active={{ bg: 'gray.900' }}
        />
      )}
      {mode === 'edit' && (
        <Box
          id="sidebar"
          position="absolute"
          top={0}
          left={0}
          h="100%"
          w="450px"
          bg="gray.100"
          borderRight="1px solid"
          borderColor="gray.200"
          transform={isToolboxOpen ? 'translateX(0)' : 'translateX(-100%)'}
          transition="transform 0.25s ease"
          pointerEvents={isToolboxOpen ? 'auto' : 'none'}
          boxShadow={isToolboxOpen ? 'md' : 'none'}
          zIndex={20}
          display="flex"
          flexDirection="column"
          // SCROLL burada: panel kendi konteyneri
          overflowX="hidden"
          overflowY="auto"
          // tuvalin scroll’ını tetikleme
          onWheelCapture={e => e.stopPropagation()}
          sx={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
        >
          {/* HEADER */}
          <Box
            position="sticky"
            top="0"
            zIndex={1}
            bg="gray.100"
            pb={2}
            mb={3}
            borderBottom="1px solid"
            borderColor="gray.300"
            textAlign="center"
          >
            <strong style={{ fontSize: 16 }}>System Flow</strong>
            <InputGroup size="sm" mt={2} px={2}>
              <InputLeftElement pointerEvents="none" color="gray.500">
                <FiSearch />
              </InputLeftElement>
              <Input
                value={toolboxQuery}
                onChange={e => setToolboxQuery(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Escape') setToolboxQuery('');
                }}
                placeholder="Bileşen ara..."
                aria-label="Bileşen ara"
                bg="white"
                borderRadius="md"
                pl="34px"
                pr={toolboxQuery ? '30px' : undefined}
              />
              {toolboxQuery && (
                <InputRightElement>
                  <IconButton
                    aria-label="Aramayı temizle"
                    icon={<FiX />}
                    size="xs"
                    variant="ghost"
                    onClick={() => setToolboxQuery('')}
                  />
                </InputRightElement>
              )}
            </InputGroup>
            <Text fontSize="xs" color="gray.600" mt={1}>
              {paletteItems.length} bileşen
              {toolboxQuery ? ` · "${toolboxQuery}"` : ''}
            </Text>
          </Box>
          {paletteItems.length === 0 && (
            <Text fontSize="sm" color="gray.500" textAlign="center" px={3}>
              Aramayla eşleşen bileşen yok.
            </Text>
          )}
          <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
            {paletteItems.map(item => (
                <Box
                  key={item.id}
                  draggable
                  onDragStart={e => handleDragStart(e, item)}
                  title={item.name || item.type}
                  cursor="grab"
                  bg="white"
                  p="2px"
                  borderRadius="5px"
                  textAlign="center"
                  boxShadow="sm"
                  position="relative"
                >
                  <img
                    src={item.imagePath}
                    alt={item.type}
                    style={{
                      width: '26px',
                      height: '26px',
                      objectFit: 'contain',
                      display: 'block',
                      margin: '0 auto',
                    }}
                  />
                  {GROUPS_WITH_TWO_BITS.includes(item.groupid) && (
                    <span
                      style={{
                        position: 'absolute',
                        top: 2,
                        right: 4,
                        background: '#3182ce',
                        color: 'white',
                        fontSize: '10px',
                        padding: '2px 6px',
                        borderRadius: '6px',
                        fontWeight: 700,
                      }}
                      title="Supports 2 registers"
                    >
                      2x
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: '12px',
                      color: 'black',
                      display: 'block',
                      marginTop: '4px',
                      wordWrap: 'break-word',
                    }}
                  >
                    {(item.name || item.type || '').substring(0, 16)}
                  </span>
                </Box>
            ))}
          </Box>
        </Box>
      )}
      <Box
        ref={diagramRef}
        onDrop={handleDrop}
        onDragOver={e => e.preventDefault()}
        onMouseMove={handleMouseMoveOnDiagram}
        onMouseUp={handleMouseUpOnDiagram}
        onMouseDown={e => {
          if (!isTextEditModalOpen && e.target === e.currentTarget) {
            setSelectedItemId(null);
          }
        }}
        onClick={e => {
          if (!isTextEditModalOpen && e.target === e.currentTarget) {
            setSelectedItemId(null);
          }
        }}
        top={isFullscreen ? '0' : 'unset'}
        left={isFullscreen ? '0' : 'unset'}
        right={isFullscreen ? '0' : 'unset'}
        bottom={isFullscreen ? '0' : 'unset'}
        width={isFullscreen ? '100vw' : '100%'}
        height={isFullscreen ? '100vh' : '100%'}
        overflow="auto"
        backgroundColor="white"
        padding="0"
        margin="0"
        position="relative"
        sx={mode === 'edit' ? { userSelect: 'none', WebkitUserSelect: 'none' } : {}}
      >
        <Box minWidth={`${maxX + 200}px`} minHeight={`${maxY + 200}px`}>
          <svg
            viewBox={`0 0 ${maxX + 200} ${maxY + 200}`}
            width={`${maxX + 200}px`}
            height={`${maxY + 200}px`}
            preserveAspectRatio="xMinYMin meet"
            style={{
              display: 'block',
              transform: mode === 'view' ? `scale(${viewScale})` : 'scale(1)',
              transformOrigin: mode === 'view' ? 'center center' : 'top left',
              backgroundColor: 'white',
            }}
          >
            {/* Kılavuz çizgiler - ileride aktif edilecek
            {mode === 'edit' && (
              <defs>
                <pattern id="grid-small" width="20" height="20" patternUnits="userSpaceOnUse">
                  <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e2e8f0" strokeWidth="0.5" />
                </pattern>
                <pattern id="grid-large" width="100" height="100" patternUnits="userSpaceOnUse">
                  <rect width="100" height="100" fill="url(#grid-small)" />
                  <path d="M 100 0 L 0 0 0 100" fill="none" stroke="#cbd5e0" strokeWidth="1" />
                </pattern>
              </defs>
            )}
            {mode === 'edit' && (
              <rect width="100%" height="100%" fill="url(#grid-large)" />
            )}
            */}
            <g id="pipes-layer">
              {droppedItems
                .filter(item =>
                  [
                    '1',
                    '2',
                    '3',
                    '4',
                    '5',
                    '6',
                    '7',
                    '8',
                    '9',
                    '10',
                    '11',
                    '201',
                    '202',
                    '203',
                    '204',
                  ].includes(item.toolboxId)
                )
                .sort((a, b) => a.y - b.y)
                .map(item => (
                  <g
                    key={item.id}
                    transform={getItemTransform(item)}
                    onMouseDown={e => handleMouseDownOnItem(e, item)}
                    onContextMenu={e => handleContextMenu(e, item)}
                    onClick={e => {
                      e.stopPropagation();

                      if (mode !== 'view') return;
                      if (item.toolboxId === '9999' && item.linkedFlowId) {
                        navigate(`/flow/view/${item.linkedFlowId}`);
                      }
                    }}
                    style={{
                      cursor: 'grab',
                      outline:
                        item.id === selectedItemId
                          ? '0.5px solid black'
                          : 'none',
                    }}
                  >
                    <image
                      href={getImagePathByToolboxId(
                        item.toolboxId,
                        item.tagInfo?.value,
                        mode,
                        item.tagInfo1?.value
                      )}
                      width={item.width * scaleFactor * 0.5}
                      height={item.height * scaleFactor * 0.5}
                      x="0"
                      y="0"
                      preserveAspectRatio="none"
                    />
                  </g>
                ))}
            </g>

            <g id="objects-layer">
              {droppedItems.map(item => renderItem(item, mode === 'edit'))}
            </g>

            {/* Seçili bileşenin döndürme/boyutlandırma tutamakları en üstte */}
            <g id="selection-layer">{renderSelectionOverlay()}</g>
          </svg>
        </Box>
        {alignmentHelper.visible &&
          mode !== 'view' &&
          (() => {
            const { left, top } = getContainerCoordsFromSvg(
              alignmentHelper.x,
              alignmentHelper.y
            );
            return (
              <>
                {/* Dikey kılavuz */}
                <Box
                  position="absolute"
                  left={`${left}px`}
                  top="0"
                  height="100%"
                  width="0"
                  bg="red"
                  pointerEvents="none"
                  zIndex={40}
                  sx={{ borderLeft: '1px dashed red' }}
                />
                {/* Yatay kılavuz */}
                <Box
                  position="absolute"
                  left="0"
                  top={`${top}px`}
                  width="100%"
                  height="0"
                  bg="red"
                  pointerEvents="none"
                  zIndex={40}
                  sx={{ borderTop: '1px dashed red' }}
                />
              </>
            );
          })()}
        {contextMenu.visible && (
          <Box
            position="absolute"
            // top={`${contextMenu.y * scaleFactor}px`}
            // left={`${contextMenu.x * scaleFactor}px`}
            top={`${contextMenu.y}px`}
            left={`${contextMenu.x}px`}
            bg="white"
            border="1px solid black"
            p="10px"
            zIndex="1000"
            onMouseLeave={handleCloseContextMenu}
          >
            {currentItem?.toolboxId === '9999' && (
              <>
                <button onClick={openFlowAssignmentModal}>
                  Assign Flow Link{' '}
                  {currentItem?.linkedFlowName &&
                    `: ${currentItem?.linkedFlowName}`}
                </button>
                <hr style={{ margin: '8px 0' }} />
              </>
            )}
            {currentItem?.toolboxId !== '200' &&
              currentItem?.toolboxId !== '9999' && (
                <>
                  <button onClick={openAssignRegisterModal}>
                    Assign Register
                    {currentItem?.tagInfo?.name &&
                      ` : ${currentItem?.tagInfo?.name}`}
                  </button>
                  <hr style={{ margin: '8px 0' }} />

                  {(() => {
                    const toolbox = toolboxItems.find(
                      t => String(t.id) === String(currentItem?.toolboxId)
                    );
                    return GROUPS_WITH_TWO_BITS.includes(toolbox?.groupid);
                  })() && (
                    <>
                      <button onClick={() => setIsAssignModal2Open(true)}>
                        Assign Register 2
                        {currentItem?.tagInfo1?.name &&
                          ` : ${currentItem?.tagInfo1?.name}`}
                      </button>
                      <hr style={{ margin: '8px 0' }} />
                    </>
                  )}
                </>
              )}

            <button onClick={handleDelete}>Delete</button>
            <hr style={{ margin: '8px 0' }} />

            <label>
              Scale (%):
              <input
                type="number"
                value={numericFieldValue('scale', 100)}
                onChange={e => commitNumericField('scale', e.target.value)}
                onBlur={() => syncNumericField('scale')}
                min={MIN_ITEM_SCALE}
                max={MAX_ITEM_SCALE}
              />
            </label>
            <hr style={{ margin: '8px 0' }} />

            <label>
              Width:
              <input
                type="number"
                value={numericFieldValue('width', MIN_ITEM_SIZE)}
                onChange={e => commitNumericField('width', e.target.value)}
                onBlur={() => syncNumericField('width')}
                min={MIN_ITEM_SIZE}
                max={MAX_ITEM_SIZE}
              />
            </label>
            <hr style={{ margin: '8px 0' }} />

            <label>
              Height:
              <input
                type="number"
                value={numericFieldValue('height', MIN_ITEM_SIZE)}
                onChange={e => commitNumericField('height', e.target.value)}
                onBlur={() => syncNumericField('height')}
                min={MIN_ITEM_SIZE}
                max={MAX_ITEM_SIZE}
              />
            </label>
            <hr style={{ margin: '8px 0' }} />

            <label>
              Rotation (°):
              <input
                type="number"
                value={numericFieldValue('rotation', 0)}
                onChange={e => commitNumericField('rotation', e.target.value)}
                onBlur={() => syncNumericField('rotation')}
                min="0"
                max="359"
                step="1"
              />
            </label>
            <Flex gap="4px" mt="6px" mb="2px">
              <Button
                size="xs"
                leftIcon={<FiRotateCcw />}
                onClick={() => rotateSelectedBy(-90)}
              >
                90°
              </Button>
              <Button
                size="xs"
                leftIcon={<FiRotateCw />}
                onClick={() => rotateSelectedBy(90)}
              >
                90°
              </Button>
              <Button size="xs" onClick={() => setItemRotation(0)}>
                Reset
              </Button>
            </Flex>
            <hr style={{ margin: '8px 0' }} />

            <button onClick={handleCloseContextMenu}>Close</button>
          </Box>
        )}

        {mode === 'edit' && (
          <Box position="fixed" bottom="20px" right="20px" zIndex={50}>
            <Button colorScheme="blue" onClick={openSaveFlowModal}>
              Save the flow
            </Button>
          </Box>
        )}

        <FlowSaveModal
          isOpen={isSaveModalOpen}
          onClose={() => setIsSaveModalOpen(false)}
          // onSave={handleSaveFlow}
          droppedItems={droppedItems}
          initialFlowName={flowName}
          flowId={flowId}
          onUpdateSuccess={() => fetchFlows()}
        />
      </Box>
      <AssignRegisterModal
        isOpen={isAssignModal2Open}
        onClose={() => setIsAssignModal2Open(false)}
        tagList={tagList}
        currentItem={currentItem}
        title="Assign Register 2"
        targetField="tagInfo1"
        selectedTagId={selectedTag1}
        setSelectedTagId={setSelectedTag1}
        onSave={(selectedDeviceId, selectedTag1Id) => {
          setDroppedItems(prevItems =>
            prevItems.map(item =>
              item.id === currentItem.id
                ? {
                    ...item,
                    deviceInfo: tagList.find(d => d.id === selectedDeviceId),
                    tagInfo1: tagList
                      .find(d => d.id === selectedDeviceId)
                      ?.tags.find(t => t.registerId === selectedTag1Id),
                  }
                : item
            )
          );
          setIsAssignModal2Open(false);
          toast({
            title: 'Register 2 Assigned',
            status: 'success',
            duration: 2000,
            isClosable: true,
          });
        }}
      />
      {mode === 'view' && (
        <Box position="absolute" bottom="20px" left="20px" zIndex="10">
          <Button onClick={() => setViewScale(prev => Math.min(prev + 0.1, 3))}>
            Zoom In
          </Button>
          <Button
            onClick={() => setViewScale(prev => Math.max(prev - 0.1, 0.5))}
            ml={2}
          >
            Zoom Out
          </Button>
          <Button onClick={() => setViewScale(1)} ml={2}>
            Reset
          </Button>
        </Box>
      )}
      <FlowAssignModal
        isOpen={isFlowAssignModalOpen}
        onClose={() => setIsFlowAssignModalOpen(false)}
        currentItem={currentItem}
        allFlows={allFlows}
        onSave={(linkedFlowId, linkedFlowName) => {
          setDroppedItems(prev =>
            prev.map(i =>
              i.id === currentItem.id
                ? { ...i, linkedFlowId, linkedFlowName }
                : i
            )
          );
        }}
      />
      <AssignRegisterModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        tagList={tagList}
        currentItem={currentItem}
        onSave={(selectedDeviceId, selectedTagId) => {
          setDroppedItems(prevItems =>
            prevItems.map(item =>
              item.id === currentItem.id
                ? {
                    ...item,
                    deviceInfo: tagList.find(d => d.id === selectedDeviceId),
                    tagInfo: tagList
                      .find(d => d.id === selectedDeviceId)
                      ?.tags.find(t => t.registerId === selectedTagId),
                  }
                : item
            )
          );
          toast({
            title: 'Assigned Successfully',
            description: 'The tag has been successfully assigned.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        }}
      />
      <TextEditModal
        isTextEditModalOpen={isTextEditModalOpen}
        setIsTextEditModalOpen={setIsTextEditModalOpen}
        currentEditingText={currentEditingText}
        setCurrentEditingText={setCurrentEditingText}
        fontSize={fontSize}
        setFontSize={setFontSize}
        textColor={textColor}
        setTextColor={setTextColor}
        backgroundColor={backgroundColor}
        setBackgroundColor={setBackgroundColor}
        borderColor={borderColor}
        setBorderColor={setBorderColor}
        dashed={dashed}
        setDashed={setDashed}
        isBold={isBold}
        setIsBold={setIsBold}
        isItalic={isItalic}
        setIsItalic={setIsItalic}
        simpleColorOnly={isEditingConnector}
        onSave={handleSaveTextEdit}
      />
    </Flex>
  );
};

export default FlowComponent;
